import { getSyncRpcTourBagMstrByPlayerId, postSaveTourBagMstrData, postSaveDeletedTourBag, getDeletedTourBagClubs, getTourBagsForPWA } from '@/api/endpoints';
import { DeletedTourBag, TourBagMstrPayload } from '@/api/models';
import { AppSettingsDB, TourBagDB } from '@/db/repositories';
import { setDBUpdatedNow, getDBUpdatedOn } from '@/db/helpers/dbMetadata';
import { OfflineQueueService, TourBagService } from '@/service';
import { OfflineQueueHelper } from '@/library';
import { onlineStatus } from '@/utils';
import { Action, HeadTypeName } from '@/constants';

export const syncTourBagData = async (playerId: number) => {
  const isOnline = onlineStatus.get();
  if (!isOnline) return;

  // No stale or forceUpdate check, always fetch Tour Bag data from the API if online

  try {
    const tourBag = await getSyncRpcTourBagMstrByPlayerId(playerId);
    await TourBagDB.update({ playerId, bags: tourBag });
  } catch (error) {
    console.error('Failed to sync getTours from API:', error);
    throw error;
  }
};

export const getTourBagData = async (playerId: number) => {
  await syncTourBagData(playerId);

  const tourBagDB = await TourBagDB.get(playerId);
  return tourBagDB?.bags;
};

export const syncAllTourBagData = async () => {
  const isOnline = onlineStatus.get();
  if (!isOnline) return;

  // No stale or forceUpdate check, always fetch Tour Bags delta data from the API if online

  try {
    const modifiedAfter = await getDBUpdatedOn('getTourBagsForPWA');
    const tourBags = await getTourBagsForPWA(modifiedAfter);
    await TourBagDB.bulkAdd(tourBags);
    await setDBUpdatedNow('getTourBagsForPWA');
  } catch (error) {
    console.error('Failed to Tour Bags from API syncTourBags:', error);
    throw error;
  }
};

export const saveTourBagData = async (playerId: number, tourBag: TourBagMstrPayload, action: Action, clubId?: number): Promise<TourBagMstrPayload | undefined> => {
  try {
    if (!onlineStatus.get()) {
      // Get the specific club, set, bsg or whatever entity the user is updating
      const entity = OfflineQueueHelper.extractEntity(tourBag, clubId);

      if (!entity) return tourBag;

      if (action === 'CREATE') {
        OfflineQueueHelper.generateMetadataForNewItem(tourBag, entity);
      }

      const offlineData = OfflineQueueHelper.buildOfflinePayload(entity, tourBag.headTypeDescription as HeadTypeName | '', tourBag);

      if (offlineData) {
        await OfflineQueueService.enqueueOfflineMutation('TOUR_BAG', action, offlineData.entityId, offlineData.entityType, offlineData.payload);
      }

      // Update full bag in IndexedDB for optimistic UI
      await TourBagDB.updateBag(playerId, tourBag);

      return tourBag;
    } else {
      // Online flow
      await postSaveTourBagMstrData(tourBag);
      await TourBagDB.updateBag(playerId, tourBag);

      // we need to fetch the specific player tourbag to get the latest version from the backend
      const tourBags = await getTourBagData(playerId);
      const updatedTourBag = tourBags?.find((bag) => bag.headTypeDescription === tourBag.headTypeDescription);

      // sync player tourbags to update the local sync date without blocking the app by wrapping it in a promise
      TourBagService.syncAllTourBagData()
        .then(async () => {
          const now = new Date();
          await AppSettingsDB.set('lastSyncDate', now.toISOString());
        })
        .catch((error) => {
          console.error('Sync failed:', error);
        });

      return updatedTourBag;
    }
  } catch (error) {
    console.error('Failed to update TourBag:', error);
    throw error;
  }
};

export const saveDeletedTourBag = async (tourBag: DeletedTourBag) => {
  try {
    await postSaveDeletedTourBag(tourBag);
  } catch (error) {
    console.error('Failed to delete TourBag:', error);
    // TODO: Save error to ErrorLogRepo
    throw error;
  }
};

export const getDeletedTourBagHistory = async (playerId = 0, headTypeId: number) => {
  const deletedClubs = await getDeletedTourBagClubs(playerId, headTypeId);
  return deletedClubs;
};
